/**
 * Email templates for CurrencyCore Admin communications.
 * Default templates are system-defined and immutable.
 * Custom templates can be created, copied from defaults, edited, and deleted.
 */

export type TemplateCategory =
  | "onboarding"
  | "support"
  | "feedback"
  | "scaling"
  | "reengagement"
  | "custom";

export interface EmailTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  subject: string;
  html: string;
  text?: string;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
  description: string;
}

export interface TemplateVariables {
  name?: string | null;
  email?: string | null;
  top_plan?: string | null;
  org_count?: number | null;
  signup_date?: string | null;
  [key: string]: string | number | null | undefined;
}

export const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  onboarding: "Onboarding",
  support: "Support & Help",
  feedback: "Feedback",
  scaling: "Usage & Scaling",
  reengagement: "Re-engagement",
  custom: "Custom",
};

/** Email container shell matching CurrencyCore brand design */
function emailShell(
  tagline: string,
  heading: string,
  contentHtml: string,
  cta?: { url: string; label: string },
): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <base target="_blank">
  <title>${heading}</title>
</head>
<body style="margin:0;padding:24px 12px;background-color:#08111f;color:#eef6f4;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;line-height:1.6;-webkit-font-smoothing:antialiased;">
  <div style="max-width:560px;margin:0 auto;background-color:#101927;border:1px solid #223246;border-radius:14px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.4);">
    <!-- Header -->
    <div style="padding:28px 32px 20px;border-bottom:1px solid #223246;background:linear-gradient(135deg, rgba(127,255,212,0.12) 0%, rgba(127,255,212,0) 60%);">
      <div style="display:inline-block;padding:4px 12px;border:1px solid rgba(127,255,212,0.35);border-radius:999px;color:#7fffd4;font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;margin-bottom:14px;">
        ${tagline}
      </div>
      <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:700;line-height:1.25;letter-spacing:-0.015em;">
        ${heading}
      </h1>
    </div>

    <!-- Body -->
    <div style="padding:28px 32px;color:#cbd5e1;font-size:15px;line-height:1.7;">
      ${contentHtml}

      ${
        cta
          ? `<div style="margin:28px 0 10px;">
        <a href="${cta.url}" style="display:inline-block;background-color:#7fffd4;color:#08111f;text-decoration:none;padding:12px 22px;border-radius:8px;font-size:14px;font-weight:700;letter-spacing:-0.01em;">
          ${cta.label} &rarr;
        </a>
      </div>`
          : ""
      }
    </div>

    <!-- Footer -->
    <div style="padding:20px 32px;border-top:1px solid #1c2a3c;background-color:#0c1420;font-size:12px;color:#64748b;line-height:1.6;">
      <p style="margin:0 0 4px;">
        Sent from <strong>CurrencyCore</strong>. Replies to this email route directly to our personal inbox at <a href="mailto:mail@currency-core.com" style="color:#7fffd4;text-decoration:none;">mail@currency-core.com</a>.
      </p>
      <p style="margin:0;color:#475569;">
        CurrencyCore &bull; Real-time, historical &amp; PPP currency intelligence
      </p>
    </div>
  </div>
</body>
</html>`;
}

export const DEFAULT_TEMPLATES: EmailTemplate[] = [
  {
    id: "default_onboarding_inactive",
    name: "Onboarding: Signed up, no usage yet",
    category: "onboarding",
    subject: "Getting started with CurrencyCore / Can I help?",
    description:
      "Outreach to users who signed up recently but haven't made API requests yet. Asks what they're building and offers personal assistance.",
    isDefault: true,
    html: emailShell(
      "CurrencyCore &bull; Founder Check-in",
      "Getting started with CurrencyCore",
      `<p style="margin-top:0;">Hi {{name}},</p>
<p>I noticed you recently signed up for CurrencyCore, and wanted to thank you for giving us a try!</p>
<p>I wanted to check in personally to see what kind of application or project you're building, and whether you ran into any friction while setting up your integration or API keys.</p>
<p>If there are specific features, currency pairs, historical depths, or SDKs you need that aren't obvious in our documentation, just reply directly to this email. I read and respond to every note.</p>
<p>If you'd like a 5-minute walkthrough or want code snippets tailored to your stack (Python, TypeScript, Go, cURL), I'd be more than happy to help.</p>
<p style="margin-bottom:0;">Best regards,<br><strong style="color:#ffffff;">Jayanth</strong><br><span style="color:#94a3b8;font-size:13px;">Founder, CurrencyCore</span></p>`,
      {
        url: "https://docs.currency-core.com",
        label: "View Quickstart Docs",
      },
    ),
  },
  {
    id: "default_integration_help",
    name: "Integration: Need a hand with your setup?",
    category: "support",
    subject: "Need a hand with your CurrencyCore integration?",
    description:
      "Support outreach for users who may have encountered errors, rate limits, or stalled during integration.",
    isDefault: true,
    html: emailShell(
      "Developer Support",
      "Need a hand with your CurrencyCore integration?",
      `<p style="margin-top:0;">Hi {{name}},</p>
<p>We're reaching out from the CurrencyCore team to make sure your API integration is going smoothly.</p>
<p>Integrating financial and foreign exchange data can sometimes have subtle edge cases (timezones, weekend market closures, base currency re-basing, or caching strategies). We want to make sure you have everything you need for production.</p>
<p>Here are a few quick resources that might help:</p>
<ul style="padding-left:20px;color:#cbd5e1;">
  <li style="margin-bottom:6px;"><a href="https://docs.currency-core.com/sdks" style="color:#7fffd4;text-decoration:none;">Official SDKs &amp; Client Libraries</a> (TypeScript, Python)</li>
  <li style="margin-bottom:6px;"><a href="https://docs.currency-core.com/convert-endpoint" style="color:#7fffd4;text-decoration:none;">Conversion &amp; Historical Rates Endpoint</a></li>
  <li style="margin-bottom:6px;"><a href="https://app.currency-core.com" style="color:#7fffd4;text-decoration:none;">Developer Dashboard &amp; API Key Management</a></li>
</ul>
<p>If you're stuck on anything or need us to look over a cURL call or payload error, simply reply to this email.</p>
<p style="margin-bottom:0;">Happy building,<br><strong style="color:#ffffff;">CurrencyCore Team</strong></p>`,
      {
        url: "https://docs.currency-core.com/code-examples",
        label: "Browse Code Examples",
      },
    ),
  },
  {
    id: "default_product_feedback",
    name: "Feedback: Product & Roadmap Check-in",
    category: "feedback",
    subject: "Quick question regarding CurrencyCore",
    description:
      "Requests feedback on the developer experience, API latency, pricing, and feature requests.",
    isDefault: true,
    html: emailShell(
      "Product Feedback",
      "How is your experience with CurrencyCore?",
      `<p style="margin-top:0;">Hi {{name}},</p>
<p>You've had an account with CurrencyCore on the <strong style="color:#7fffd4;">{{top_plan}}</strong> plan, and I'd love to hear your honest thoughts so far.</p>
<p>We are actively building out our upcoming product roadmap and want to prioritize what real developers and finance teams need most:</p>
<ul style="padding-left:20px;color:#cbd5e1;">
  <li style="margin-bottom:6px;">Are there any currency pairs or historical date ranges missing for your needs?</li>
  <li style="margin-bottom:6px;">How has the latency and reliability been in your environment?</li>
  <li style="margin-bottom:6px;">Is there anything that felt clunky or could be documented better?</li>
</ul>
<p>Even just one or two sentences in reply to this email would be immensely helpful for us.</p>
<p style="margin-bottom:0;">Thanks so much for your time,<br><strong style="color:#ffffff;">Jayanth</strong><br><span style="color:#94a3b8;font-size:13px;">Founder, CurrencyCore</span></p>`,
    ),
  },
  {
    id: "default_usage_milestone",
    name: "Growth: Scaling your API volume",
    category: "scaling",
    subject: "Scaling your CurrencyCore usage",
    description:
      "Congratulates active users on growing volume and offers tailored quotas or higher tiers to prevent 429 blocks.",
    isDefault: true,
    html: emailShell(
      "Volume &amp; Scaling",
      "Scaling your CurrencyCore API usage",
      `<p style="margin-top:0;">Hi {{name}},</p>
<p>Congrats on the volume and growth you're seeing through CurrencyCore!</p>
<p>We noticed your API traffic is actively scaling on the <strong style="color:#7fffd4;">{{top_plan}}</strong> plan. Our priority is ensuring your production services run without hitting 429 rate limit ceilings or unexpected request blocks.</p>
<p>If you're anticipating spikes, launching a new product, or need a dedicated custom tier with higher concurrency and guaranteed SLA, we'd love to set that up for you.</p>
<p>Let us know what throughput or monthly quota your team is targeting by replying directly to this email.</p>
<p style="margin-bottom:0;">Cheers,<br><strong style="color:#ffffff;">CurrencyCore Team</strong></p>`,
      {
        url: "https://app.currency-core.com/plans",
        label: "Review Plan Quotas",
      },
    ),
  },
  {
    id: "default_reengagement_checkin",
    name: "Re-engagement: Can we do better?",
    category: "reengagement",
    subject: "Following up on CurrencyCore",
    description:
      "Respectful, low-pressure follow-up to understand why a user hasn't returned and how the service can improve.",
    isDefault: true,
    html: emailShell(
      "Checking In",
      "Did CurrencyCore miss the mark?",
      `<p style="margin-top:0;">Hi {{name}},</p>
<p>I noticed it's been a little while since you created your CurrencyCore account, and you haven't been active recently.</p>
<p>No sales pitch: I just wanted to ask if CurrencyCore was not the right fit for what you were trying to accomplish? If pricing, data coverage, developer experience, or anything else fell short, I would really appreciate hearing your candid feedback so we can do better.</p>
<p>If your project requirements changed or you already found another solution, no problem at all. If you're still planning to build and need some guidance, feel free to reply anytime.</p>
<p style="margin-bottom:0;">Thanks for your time,<br><strong style="color:#ffffff;">Jayanth</strong><br><span style="color:#94a3b8;font-size:13px;">Founder, CurrencyCore</span></p>`,
    ),
  },
];

/**
 * Remove all em dashes (HTML entities or Unicode) and replace with safe alternatives.
 */
function removeEmDashes(str: string): string {
  return str
    .replace(/&mdash;/g, " - ")
    .replace(/\u2014/g, " - ")
    .replace(/—/g, " - ");
}

/**
 * Strip HTML tags to create plain text email alternative
 */
export function stripHtml(html: string): string {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<a\s+[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, "$2 ($1)")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li>/gi, "&bull; ")
    .replace(/<\/h[1-6]>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&bull;/g, "•")
    .replace(/&mdash;/g, " - ")
    .replace(/—/g, " - ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&rarr;/g, "->")
    .replace(/&bull;/g, "•")
    .replace(/[ \t]+/g, " ")
    .replace(/\n\s*\n\s*\n/g, "\n\n")
    .trim();
}

/**
 * Interpolate dynamic variables (e.g. {{name}}, {{email}}) in subject and HTML/text.
 */
export function renderTemplate(
  template: Pick<EmailTemplate, "subject" | "html" | "text">,
  vars: TemplateVariables,
): { subject: string; html: string; text: string } {
  const name = vars.name?.trim() || "there";
  const email = vars.email?.trim() || "";
  const topPlan = vars.top_plan ? String(vars.top_plan).toUpperCase() : "FREE";
  const orgCount = String(vars.org_count ?? 0);
  const signupDate = vars.signup_date ? String(vars.signup_date).slice(0, 10) : "recently";

  const replaceMap: Record<string, string> = {
    name,
    email,
    top_plan: topPlan,
    org_count: orgCount,
    signup_date: signupDate,
  };

  const interpolate = (str: string): string => {
    return str.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key: string) => {
      if (key in replaceMap) return replaceMap[key]!;
      if (vars[key] !== undefined && vars[key] !== null) return String(vars[key]);
      return "";
    });
  };

  const rawSubject = interpolate(template.subject);
  const rawHtml = interpolate(template.html);
  const rawText = template.text ? interpolate(template.text) : stripHtml(rawHtml);

  return {
    subject: removeEmDashes(rawSubject),
    html: removeEmDashes(rawHtml),
    text: removeEmDashes(rawText),
  };
}
